import { AuthError, AuthErrorType, ValidationErrorType } from '../types';
import l10n from '@built/i18n';

export const UNKNOWN_ERROR_TITLE = 'Unknown error';
export const UNKNOWN_ERROR_MESSAGE = 'An unknown error occurred';

// These are messages shown when form validation errors occur
export const getValidationError = (type: ValidationErrorType) => {
  switch (type) {
    case ValidationErrorType.VALIDATION_EMAIL_ERROR:
      return l10n.t(
        'EmailField:info.invalid',
        'Please enter a valid email address.'
      );
    case ValidationErrorType.VALIDATION_PASSWORD_ERROR:
      return l10n.t(
        'EmailField:info.enterPassword',
        'Please enter a password.'
      );
    default:
      return UNKNOWN_ERROR_MESSAGE;
  }
};

/**
 * Utility method to generate AuthData-specific errors based on
 * AuthErrorType types; AuthErrors are typically used as banner
 * or notification style messaging in the app that have both
 * a title and a body/message
 *
 * @param type
 * @param message optional message override
 * @param title optional title override
 */
// tslint:disable-next-line:cyclomatic-complexity
export function getAuthError(
  type: AuthErrorType,
  message?: string,
  title?: string
): AuthError {
  let _message: string;
  let _title: string;
  switch (type) {
    case AuthErrorType.ACCOUNT_DISABLED:
      _message = l10n.t(
        'PasswordReset:error.accountDisabledMessage',
        'Your account has been disabled.'
      );
      _title = l10n.t(
        'PasswordReset:error.accountDisabledTitle',
        'Account disabled.'
      );
      break;
    case AuthErrorType.ACCOUNT_EXPIRED:
      _message = l10n.t(
        'PasswordReset:error.accountExpiredMessage',
        `Your account has been suspended due to an incomplete password
        reset during account transition.`
      );
      _title = l10n.t(
        'PasswordReset:error.accountExpiredTitle',
        'Account suspended.'
      );
      break;
    case AuthErrorType.ATTEMPTS_EXCEEDED_ERROR:
      _message = l10n.t(
        'PasswordReset:error.attemptsExceededMessage',
        `For security reasons, your account has been temporarily
disabled due to an excessive number of failed login attempts. Please try
again in a few minutes.`
      );
      _title = l10n.t(
        'PasswordReset:error.attemptsExceededTitle',
        'Your account has been temporarily disabled.'
      );
      break;
    case AuthErrorType.EMAIL_VERIFICATION_ERROR:
      _message = l10n.t(
        'PasswordReset:error.emailVerificationMessage',
        "The email verification link doesn't appear to be working."
      );
      _title = l10n.t(
        'PasswordReset:error.emailVerificationTitle',
        'Something went wrong.'
      );
      break;
    case AuthErrorType.INVALID_CREDENTIALS_ERROR:
      _message = l10n.t(
        'PasswordReset:error.invalidCredentialsMessage',
        'The username or password you entered is incorrect.'
      );
      _title = l10n.t(
        'PasswordReset:error.invalidCredentialsTitle',
        'The credentials you entered are invalid.'
      );
      break;
    case AuthErrorType.LIMIT_EXCEEDED_ERROR:
      _message = l10n.t(
        'PasswordReset:error.limitExceededMessage',
        'Please wait a few minutes and try again.'
      );
      _title = l10n.t(
        'PasswordReset:error.limitExceededTitle',
        'Exceeded password reset attempts.'
      );
      break;
    case AuthErrorType.MOCK_COGNITO_ERROR:
      _message = l10n.t(
        'PasswordReset:error.mock.mockCognitoMessage',
        'A mock cognito related error occurred.'
      );
      _title = l10n.t(
        'PasswordReset:error.mock.mockCognitoTitle',
        'Mock cognito error'
      );
      break;
    case AuthErrorType.PASSWORD_RESET_REQUIRED_ERROR:
      _message = l10n.t(
        'PasswordReset:error.resetRequiredMessage',
        `Your account has been disabled and requires you to reset your
password. Please check your email for instructions on how to complete the password
reset process.`
      );
      _title = l10n.t(
        'PasswordReset:error.resetRequiredTitle',
        'Please reset your password.'
      );
      break;
    case AuthErrorType.REGISTRATION_ERROR:
      _message = l10n.t(
        'PasswordReset:error.registrationErrorMessage',
        'An error occurred during the registration process.'
      );
      _title = l10n.t(
        'PasswordReset:error.registrationErrorTitle',
        'Registration error.'
      );
      break;
    case AuthErrorType.SAML_RESPONSE_ERROR:
      _message = l10n.t(
        'PasswordReset:error.samlErrorMessage',
        `An error occurred while processing your SSO request. If
this continues, please contact your IT administrator.`
      );
      _title = l10n.t(
        'PasswordReset:error.samlErrorTitle',
        'Single sign-on error.'
      );
      break;
    case AuthErrorType.TOKEN_EXPIRED:
      _message = l10n.t(
        'PasswordReset:error.tokenExpiredMessage',
        'Your session has expired and you will need to login again.'
      );
      _title = l10n.t(
        'PasswordReset:error.tokenExpiredTitle',
        'Session expired.'
      );
      break;
    case AuthErrorType.MFA_VERIFICATION_ERROR:
      _message = l10n.t(
        'Auth:Mfa.errors.invalidCodeError',
        'Please check the code you entered and try again.'
      );
      _title = l10n.t('Auth:Mfa.errors.invalidCodeTitle', 'Invalid MFA code.');
      break;
    case AuthErrorType.SESSION_EXPIRED:
      _message = l10n.t(
        'Auth:Mfa.errors.expiredCodeError',
        'The code you entered is expired. Please click the link below to request a new one.'
      );
      _title = l10n.t('Auth:Mfa.errors.expiredCodeTitle', 'Expired MFA code.');
      break;
    default:
      _message = UNKNOWN_ERROR_MESSAGE;
      _title = UNKNOWN_ERROR_TITLE;
      break;
  }
  // override with passed in message
  if (message) {
    _message = message;
  }
  // override with passed in title
  if (title) {
    _title = title;
  }

  return {
    message: _message,
    type: type,
    title: _title
  };
}



// WEBPACK FOOTER //
// ./src/utils/errors.ts